import { moment } from "@msuite/utils/date";
import { download } from "@msuite/utils/files";
import { guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { Button } from "../button";
import { useLoading } from "../hooks";
import { Icon } from "../icons";
import { useTranslation } from "../localization";
import { FileIcon } from "./file-icon";
import { Preview } from "./preview";

/** Props Interface */
export interface FileCardLocalProps extends VariantProps<typeof fileCardLocalClasses> {
	file: Nullable<File>;
	onRemove?: (file: File) => void;
	onDelete?: (file: File) => void;
	onRename?: (file: File, name: string) => void | Promise<void>;
	removeType?: "remove" | "unlink" | "delete";
	withDownload?: boolean;
	withRemove?: boolean;
	withDelete?: boolean;
	width: Nullable<number>;
	height: Nullable<number>;
	/**
	 * What the card is titled — a document named by what it is rather than by its file. The
	 * file's name when absent.
	 */
	title?: ReactNode;
	/** The line beneath the title; the file's last change when absent. */
	caption?: ReactNode;
}

export function FileCardLocal({
	file,
	title,
	caption,
	onRemove,
	onDelete,
	removeType = "remove",
	withDownload = false,
	withRemove = true,
	withDelete = false,
	width,
	height,
	variant,
}: FileCardLocalProps) {
	/** Constants */
	const isFile = !!file?.name;

	/** Hooks */
	const removeLoader = useLoading();
	const deleteLoader = useLoading();
	const downloadLoader = useLoading();
	const { language } = useTranslation();

	/** Functions */
	async function handleOnDownload() {
		try {
			downloadLoader.start();
			await download(file);
			downloadLoader.stop();
		} catch (error) {
			console.error("Error downloading file:", error);
		} finally {
			downloadLoader.stop();
		}
	}

	async function handleOnRemove() {
		try {
			removeLoader.start();
			if (!file) return;
			if (!onRemove) return;
			await onRemove(file);
		} catch (error) {
			console.error("Error removing file:", error);
		} finally {
			removeLoader.stop();
		}
	}

	async function handleOnDelete() {
		try {
			deleteLoader.start();
			if (!file) return;
			if (!onDelete) return;
			await onDelete(file);
		} catch (error) {
			console.error("Error deleting file:", error);
		} finally {
			deleteLoader.stop();
		}
	}

	/** Render */
	return (
		<div className="w-full">
			<Preview
				file={file}
				height={height}
				width={width}
			>
				<div
					className={fileCardLocalClasses({ variant })}
					id={file?.name}
					style={{
						contentVisibility: "auto",
					}}
				>
					<div className="flex flex-row items-center gap-4 w-full">
						<div className="text-xl">
							<FileIcon
								fileType={file?.type}
								fileName={file?.name}
							/>
						</div>
						<div className="flex flex-col flex-1 w-full">
							<p className="block w-full text-color line-clamp-1">
								{title ?? guardString(file?.name)}
							</p>
							<p className="opacity-50 text-xs">
								{caption ?? moment(file?.lastModified).locale(language).format("LLLL")}
							</p>
						</div>
						<div className="gap-0 h-stack">
							{withRemove && onRemove && isFile ? (
								<Button
									icon={
										removeType === "unlink" ? (
											<Icon name="Unlink" />
										) : removeType === "remove" ? (
											<Icon name="X" />
										) : (
											<Icon name="Trash2" />
										)
									}
									onClick={handleOnRemove}
									variant={removeType === "delete" ? "danger-ghost" : "ghost"}
									isLoading={removeLoader.isLoading}
									stopPropagation
								/>
							) : null}
							{withDelete && onDelete && isFile ? (
								<Button
									icon={<Icon name="Trash2" />}
									onClick={handleOnDelete}
									variant="danger-ghost"
									isLoading={deleteLoader.isLoading}
									stopPropagation
								/>
							) : null}
							{withDownload && isFile ? (
								<Button
									icon={<Icon name="Download" />}
									onClick={handleOnDownload}
									isLoading={downloadLoader.isLoading}
									variant="ghost"
									stopPropagation
								/>
							) : null}
						</div>
					</div>
				</div>
			</Preview>
		</div>
	);
}

const fileCardLocalClasses = cva(
	["flex flex-col gap-2", "px-4 py-3 border-color card", "rounded-xl", "text-sm"],
	{
		variants: {
			variant: {
				default: [],
				// Pulls the card up under a ContentCard's header so the two share one border.
				"content-card": ["rounded-md! -mx-2 -mb-2 -translate-y-[0.65px]"],
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);
